<script setup>
import { reactive, ref, unref, watch } from "vue";
import { postRequest } from "@/utils/request.js";
import { message } from "ant-design-vue";

const emit = defineEmits(["ok"]);
const title = ref("");
const open = ref(false);
const confirmLoading = ref(false);
const formModel = ref({});
const formRef = ref();
const isAudit = ref(false);
const rules = reactive({
  auditAuthenticationStatus: [
    {
      required: true,
      message: "请选择审核结果",
      trigger: "blur",
    },
  ],
  auditRejectReason: [
    {
      required: false,
      message: "请输入驳回原因",
      trigger: "blur",
    },
  ],
});

watch(
  () => formModel.value.auditAuthenticationStatus,
  (authenticationStatus) => {
    if (authenticationStatus === "DONE") {
      rules.auditRejectReason[0].required = false;
    } else if (authenticationStatus === "REJECT") {
      rules.auditRejectReason[0].required = true;
    }
  },
);

const handleAudit = (record) => {
  formModel.value = Object.assign(
    {},
    { ...record },
    { auditRejectReason: "", auditAuthenticationStatus: "" },
  );
  open.value = true;
};

const handleConfirm = () => {
  formRef.value.validate().then(() => {
    const response = unref(formModel);
    postRequest("/company/temp/audit", response).then((res) => {
      if (res.success) {
        message.success("审核成功");
        handleClose();
        emit("ok");
      }
    });
  });
};

const handleClose = () => {
  open.value = false;
  formRef.value.resetFields();
};

defineExpose({
  handleAudit,
  handleClose,
  title,
  isAudit,
});
</script>

<template>
  <a-modal
    width="1200px"
    :open="open"
    :title="title"
    :confirm-loading="confirmLoading"
    @cancel="handleClose"
    @ok="handleConfirm"
  >
    <a-descriptions bordered size="small">
      <a-descriptions-item label="企业/个人名称">
        {{ formModel.name }}
      </a-descriptions-item>
      <a-descriptions-item label="类型">
        <template v-if="formModel.type === 'COMPANY'">
          <a-tag color="blue">公司</a-tag>
        </template>
        <template v-else-if="formModel.type === 'PERSON'">
          <a-tag color="green">个人</a-tag>
        </template>
      </a-descriptions-item>
      <a-descriptions-item label="统一社会信用代码/身份证号">
        <template v-if="formModel.type === 'PERSON'">
          {{ formModel.idCard }}
        </template>
        <template v-else>
          {{ formModel.code }}
        </template>
      </a-descriptions-item>
      <a-descriptions-item label="认证信息" :span="3">
        <template v-if="formModel.type === 'PERSON'">
          <a-image-preview-group>
            <a-image
              width="100px"
              :src="$imageUrl(formModel.idCardBack)"
            ></a-image>
            <a-image
              width="100px"
              :src="$imageUrl(formModel.idCardFront)"
            ></a-image>
          </a-image-preview-group>
        </template>
        <template v-else>
          <a-image-preview-group>
            <a-image
              width="100px"
              :src="$imageUrl(formModel.businessLicense)"
            ></a-image>
          </a-image-preview-group>
        </template>
      </a-descriptions-item>
      <template v-if="!isAudit">
        <a-descriptions-item label="认证状态">
          <a-tag v-if="formModel.authenticationStatus === 'DONE'" color="green">
            已认证
          </a-tag>
          <a-tag
            v-else-if="formModel.authenticationStatus === 'DOING'"
            color="gray"
          >
            待审核
          </a-tag>
          <a-tag
            v-else-if="formModel.authenticationStatus === 'REJECT'"
            color="red"
          >
            审核驳回
          </a-tag>
          <a-tag
            v-else-if="formModel.authenticationStatus === 'NOT_DONE'"
            color="gray"
          >
            未认证
          </a-tag>
          <a-tag v-else color="gray">
            {{ formModel.authenticationStatus }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="驳回原因">
          {{ formModel.rejectReason }}
        </a-descriptions-item>
      </template>
    </a-descriptions>
    <a-form
      v-if="isAudit"
      style="margin-top: 10px"
      ref="formRef"
      :labelCol="{ span: 3 }"
      :wrapperCol="{ span: 24 }"
      :model="formModel"
      :rules="rules"
    >
      <a-form-item label="审核结果" name="auditAuthenticationStatus">
        <a-radio-group v-model:value="formModel.auditAuthenticationStatus">
          <a-radio value="DONE"> 通过</a-radio>
          <a-radio value="REJECT"> 驳回</a-radio>
        </a-radio-group>
      </a-form-item>
      <a-form-item
        v-if="formModel.auditAuthenticationStatus === 'REJECT'"
        label="驳回原因"
        name="auditRejectReason"
      >
        <a-input
          v-model:value="formModel.auditRejectReason"
          placeholder="请输入驳回原因"
        />
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<style scoped lang="scss"></style>
